import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { MiniCalendar } from '@mantine/dates';
import { Stack, Paper, Grid, Button, Text, Container, Center, Loader, Flex, Title, Tabs, Box, Badge, Group, Affix, Transition, Anchor, Image, Select, Divider } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { CalendarEvent, PublicSchedule, TimeFormat } from '../../utils/schedule';
import { getAvailableTimeSlots } from '../../utils/schedule';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { IconCheck, IconMoon, IconSun, IconSunrise, IconSunset, IconX } from '@tabler/icons-react';
import { HOST } from '../../utils/const';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);
dayjs.extend(timezone);

const MINI_CALENDAR_DAYS = 7;

const dateToDateTzString = (date: dayjs.Dayjs, userTimezone: string) => {
    const tzDate = dayjs().tz(userTimezone);
    return `${date.format("YYYY-MM-DD")}/${tzDate.utcOffset()}`
}

function calendarMonthPrefetchKey(year: number, monthIndex: number, userTimezone: string): string {
    return dateToDateTzString(dayjs(new Date(year, monthIndex, 1)), userTimezone);
}

function slotHour(time: string): number {
    return Number.parseInt(time.split(':')[0], 10);
}

/** Label matches the MiniCalendar cell: Y-M-D from the picker, not `Date` instant (avoids wrong day when browser TZ ≠ `timeZone`). */
function formatDateLongInTimezone(selectedDate: Date, timeZone: string): string {
    const ymd = dayjs(selectedDate).format('YYYY-MM-DD');
    const noonInZone = dayjs.tz(`${ymd} 12:00:00`, timeZone);
    return new Intl.DateTimeFormat(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone,
    }).format(noonInZone.toDate());
}

function SelectorPanel({ availableSlots, selectedTime, setSelectedTime, timeFormat }: {
    availableSlots: string[], selectedTime: string | null, setSelectedTime: (time: string) => void, timeFormat: TimeFormat
}) {
    const span = availableSlots.length === 1 ? 12 : 6;
    const getTimeInFormat = (time: string) => {
        if(timeFormat === TimeFormat.h24) {
            return time
        }
        const [hour, minute] = time.split(':').map(Number);
        return dayjs().set('hour', hour).set('minute', minute).format('h:mm A');
    }
    return (

        <Grid gap="xs">
            {availableSlots.map((time) => (
                <Grid.Col key={time} span={span}>
                    <Button
                        variant={selectedTime === time ? 'filled' : 'outline'}
                        fullWidth
                        onClick={() => setSelectedTime(time)}
                    >
                        {getTimeInFormat(time)}
                    </Button>
                </Grid.Col>
            ))}
        </Grid>
    )
}

type PeriodTab = { value: string; label: string; icon: ReactNode; slots: string[] };

function buildPeriodTabs(availableSlots: string[]): PeriodTab[] {
    const morningSlots = availableSlots.filter((t) => slotHour(t) < 7);
    const afternoonSlots = availableSlots.filter((t) => slotHour(t) >= 7 && slotHour(t) < 12);
    const eveningSlots = availableSlots.filter((t) => slotHour(t) >= 12 && slotHour(t) < 17);
    const nightSlots = availableSlots.filter((t) => slotHour(t) >= 17);
    return [
        { value: 'morning', label: '0am–7am', icon: <IconMoon size={16} />, slots: morningSlots },
        { value: 'afternoon', label: '7am–12pm', icon: <IconSunrise size={16} />, slots: afternoonSlots },
        { value: 'evening', label: '12pm–5pm', icon: <IconSun size={16} />, slots: eveningSlots },
        { value: 'night', label: '5pm–midnight', icon: <IconSunset size={16} />, slots: nightSlots },
    ].filter((p) => p.slots.length > 0);
}

function TimeSlotSelector({ availableSlots, selectedTime, setSelectedTime, timeFormat }: {
    availableSlots: string[], selectedTime: string | null, setSelectedTime: (time: string) => void, timeFormat: TimeFormat
}) {
    const isDesktop = useMediaQuery('(min-width: 48em)');

    if (availableSlots.length === 0) {
        return (
            <Text c="dimmed" size="sm" ta="center">
                No times available for this day.
            </Text>
        );
    }

    if (availableSlots.length <= 14) {
        return (
            <SelectorPanel
                availableSlots={availableSlots}
                selectedTime={selectedTime}
                setSelectedTime={setSelectedTime}
                timeFormat={timeFormat}
            />
        );
    }

    const periodTabs = buildPeriodTabs(availableSlots);

    if (periodTabs.length <= 1) {
        return (
            <SelectorPanel
                availableSlots={periodTabs[0]?.slots ?? availableSlots}
                selectedTime={selectedTime}
                setSelectedTime={setSelectedTime}
                timeFormat={timeFormat}
            />
        );
    }

    const defaultTab = periodTabs[0].value;
    const tabsKey = `${periodTabs.map((p) => p.value).join('-')}-${availableSlots[0] ?? ''}-${availableSlots.length}`;

    return (
        <Tabs
            key={tabsKey}
            defaultValue={defaultTab}
            orientation={isDesktop ? 'vertical' : 'horizontal'}
            variant="default"
        >
            <Tabs.List
                miw={{ base: undefined, sm: 210 }}
                maw={{ base: undefined, sm: 210 }}
                w={{ base: '100%', sm: 210 }}
                flex={{ base: 1, sm: '0 0 210px' }}
                grow={!isDesktop}
            >
                {periodTabs.map(({ value, label, icon, slots }) => (
                    <Tabs.Tab key={value} value={value} leftSection={icon}>
                        <Group justify="space-between" w="100%" wrap="nowrap" gap={6}>
                            <Text span size="sm">
                                {label}
                            </Text>
                            <Badge size="sm">{slots.length}</Badge>
                        </Group>
                    </Tabs.Tab>
                ))}
            </Tabs.List>
            <Box pl={{ base: 0, sm: 'xs' }} pt={{ base: 'sm', sm: 0 }}>
                {periodTabs.map(({ value, slots }) => (
                    <Tabs.Panel value={value} key={value}>
                        <SelectorPanel
                            availableSlots={slots}
                            selectedTime={selectedTime}
                            setSelectedTime={setSelectedTime}
                            timeFormat={timeFormat}
                        />
                    </Tabs.Panel>
                ))}
            </Box>
        </Tabs>
    );
}

export const Scheduler = ({ schedule, existingEvents, onConfirm, onUpdate, currentBooking }: { schedule: PublicSchedule, existingEvents: CalendarEvent[], currentBooking: CalendarEvent | null, onConfirm: (isoDateTime: Date) => void, onUpdate: (isoDateTime: Date) => void }) => {
    const { t } = useTranslation(['app']);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
    const [availableSlots, setAvailableSlots] = useState<string[]>([]);
    const [availableSlotsPerDay, setAvailableSlotsPerDay] = useState<{ [key: string]: string[] }>({});
    const [seenMonths, setSeenMonths] = useState<Set<string>>(new Set());
    const [userTimezone, setUserTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone)
    const [timeFormat, setTimeFormat] = useState<TimeFormat>((localStorage.getItem('timeFormat') || TimeFormat.h12) as TimeFormat)
    const [windowStart, setWindowStart] = useState<string>(() => dayjs().format('YYYY-MM-DD'));

    const updateTimeFormat = (value: TimeFormat) => {
        localStorage.setItem('timeFormat', value);
        setTimeFormat(value);
    }

    const shouldDisableDate = (date: Date) => {
        const now = dayjs().add(schedule.min_number_of_hours_in_advance, 'hours');
        const nowMidnight = dayjs().startOf('day');
        const maxDate = now.add(schedule.max_number_of_days_in_advance, 'days');
        const dateToCheck = dayjs(date);
        const start = dayjs(schedule.start);
        return (
            dateToCheck.isBefore(nowMidnight, 'day') ||
            dateToCheck.isAfter(maxDate, 'day') ||
            dateToCheck.isBefore(start.subtract(schedule.min_number_of_hours_in_advance, 'hours'), 'day') ||
            (!!schedule.end && dateToCheck.isAfter(dayjs(schedule.end))) ||
            availableSlotsPerDay[dateToDateTzString(dateToCheck, userTimezone)] === undefined ||
            availableSlotsPerDay[dateToDateTzString(dateToCheck, userTimezone)].length === 0
        );
    };

    const bookingMinDay = () => {
        const nowMidnight = dayjs().startOf('day');
        const scheduleStartDay = dayjs(schedule.start).startOf('day');
        return nowMidnight.isAfter(scheduleStartDay) ? nowMidnight : scheduleStartDay;
    };

    const bookingMaxDay = () => {
        const maxFromAdvance = dayjs()
            .add(schedule.min_number_of_hours_in_advance, 'hours')
            .add(schedule.max_number_of_days_in_advance, 'days');
        if (!schedule.end) return maxFromAdvance.startOf('day');
        const endDay = dayjs(schedule.end).startOf('day');
        const cap = maxFromAdvance.startOf('day');
        return endDay.isBefore(cap) ? endDay : cap;
    };

    const cbConfirmClicked = () => {
        if (selectedDate && selectedTime) {
            const date = dayjs(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), parseInt(selectedTime.split(':')[0]), parseInt(selectedTime.split(':')[1]))).tz(userTimezone, true);
            if (currentBooking) {
                onUpdate(date.toDate());
            } else {
                onConfirm(date.toDate());
            }
        }
    }

    const handleUpdateAvailableSlots = () => {
        if (selectedDate && schedule) {
            const dateString = dateToDateTzString(dayjs(selectedDate), userTimezone);
            setAvailableSlots(availableSlotsPerDay[dateString] ?? []);
        }
    }

    useEffect(() => {
        if (!schedule) return;

        const minDay = bookingMinDay();
        const maxDay = bookingMaxDay();

        setWindowStart((prev) => {
            let next = prev;
            if (dayjs(next).isBefore(minDay, 'day')) {
                next = minDay.format('YYYY-MM-DD');
            }
            if (dayjs(next).add(MINI_CALENDAR_DAYS - 1, 'day').isAfter(maxDay, 'day')) {
                const shifted = maxDay.subtract(MINI_CALENDAR_DAYS - 1, 'day');
                next = (shifted.isBefore(minDay, 'day') ? minDay : shifted).format('YYYY-MM-DD');
            }
            return next;
        });
    }, [schedule, userTimezone]);

    useEffect(() => {
        if (!schedule) return

        const monthsNeeded = new Set<string>()
        for (let i = 0; i < MINI_CALENDAR_DAYS; i++) {
            const d = dayjs(windowStart).add(i, 'day')
            monthsNeeded.add(calendarMonthPrefetchKey(d.year(), d.month(), userTimezone))
        }
        if (selectedDate) {
            const d = dayjs(selectedDate)
            monthsNeeded.add(calendarMonthPrefetchKey(d.year(), d.month(), userTimezone))
        }

        const pending = Array.from(monthsNeeded).filter((k) => !seenMonths.has(k))
        if (pending.length === 0) {
            handleUpdateAvailableSlots()
            return
        }

        const availableSlotsPatch: { [key: string]: string[] } = {}
        const lastBookableDay = dayjs()
            .add(schedule.min_number_of_hours_in_advance, 'hours')
            .add(schedule.max_number_of_days_in_advance, 'days');

        for (const monthKey of pending) {
            const ymdPart = monthKey.split('/')[0]
            const [y, m] = ymdPart.split('-').map(Number)
            const monthIndex = m - 1
            const daysInMonth = new Date(y, monthIndex + 1, 0).getDate()

            for (let day = 1; day <= daysInMonth; day++) {
                const loopDate = dayjs(new Date(y, monthIndex, day))
                if (loopDate.isAfter(lastBookableDay, 'day')) {
                    break
                }
                const key = dateToDateTzString(loopDate, userTimezone)
                availableSlotsPatch[key] = getAvailableTimeSlots(schedule, loopDate.toDate(), existingEvents, userTimezone)
            }
        }

        setSeenMonths((prev) => {
            const next = new Set(prev)
            pending.forEach((k) => next.add(k))
            return next
        })
        setAvailableSlotsPerDay((prev) => ({ ...prev, ...availableSlotsPatch }))
    }, [schedule, userTimezone, windowStart, existingEvents, selectedDate]);

    useEffect(() => {
        handleUpdateAvailableSlots()
    }, [selectedDate, schedule, availableSlotsPerDay]);

    const minCal = bookingMinDay().format('YYYY-MM-DD')
    const maxCal = bookingMaxDay().format('YYYY-MM-DD')

    const selectedDateLabel = useMemo(() => {
        if (!selectedDate) return null;
        return formatDateLongInTimezone(selectedDate, userTimezone);
    }, [selectedDate, userTimezone]);

    return (
        <Container size="lg" p={{ base: 'md', md: 'xl' }}>
            {schedule === null && <Center><Loader /></Center>}
            {schedule !== null &&
                <Stack align="stretch" gap="md">
                    <Group justify='space-between'>
                        <Stack gap='2'>
                            <Title size="lg" order={1} fw={500}>{schedule.name}</Title>
                            <Text size="sm" c="dimmed">{schedule.description}</Text>
                        </Stack>
                        <Stack gap='0'>
                            <Text size='xs' c='dimmed'>Powered by </Text>
                            <Anchor href={HOST} target="_blank"><Group gap='4'><Image src={'/logo-small.png'} alt='Calensync' w={'16px'} />Calensync</Group></Anchor>
                        </Stack>
                    </Group>
                    {currentBooking && (
                        <Paper shadow="md" p="md" withBorder radius="md">
                            <Text c='dimmed' fw={500}>{t('schedule.current-booking')}</Text>
                            <Text>{currentBooking.start.toDate().toLocaleDateString()} @ {currentBooking.start.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {currentBooking.end.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ({Intl.DateTimeFormat().resolvedOptions().timeZone})</Text>
                            <Text mt='8' c='dimmed' size='xs'>{currentBooking.start.toDate().toUTCString()} - {currentBooking.end.toDate().toUTCString()}</Text>
                        </Paper>
                    )}
                    <Paper shadow="md" p={{ base: 'md', md: 'lg' }} withBorder radius="md" id="booking-scheduler-card">
                        <Stack gap="lg">
                            <Stack gap="sm" align="center">
                                <Text size="sm" fw={500} c="dimmed" ta="center">
                                    Pick a date
                                </Text>
                                <Center w="100%">
                                    <Box maw={720} w="100%" style={{ display: 'flex', justifyContent: 'center' }}>
                                        <MiniCalendar
                                            date={windowStart}
                                            onDateChange={setWindowStart}
                                            value={selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : null}
                                            onChange={(d) => {
                                                setSelectedDate(dayjs(d).toDate());
                                                setSelectedTime(null);
                                            }}
                                            numberOfDays={MINI_CALENDAR_DAYS}
                                            minDate={minCal}
                                            maxDate={maxCal}
                                            size="md"
                                            w="100%"
                                            maw={560}
                                            style={{ justifyContent: 'center' }}
                                            getDayProps={(dateStr) => {
                                                const day = dayjs(dateStr);
                                                const d = day.toDate();
                                                const outside =
                                                    day.isBefore(dayjs(minCal), 'day') ||
                                                    day.isAfter(dayjs(maxCal), 'day');
                                                const disabled = outside || shouldDisableDate(d);
                                                // MiniCalendar only uses min/max for "active" styling; days disabled here
                                                // would still look clickable unless we mute them explicitly.
                                                if (!disabled) {
                                                    return {};
                                                }
                                                const key = dateToDateTzString(day, userTimezone);
                                                const slots = availableSlotsPerDay[key];
                                                const noTimeSlots =
                                                    slots !== undefined && slots.length === 0;
                                                return {
                                                    disabled: true,
                                                    'data-disabled': true,
                                                    style: {
                                                        opacity: noTimeSlots && !outside ? 0.42 : outside ? 0.35 : 0.45,
                                                        color: 'var(--mantine-color-dimmed)',
                                                        cursor: 'not-allowed',
                                                    },
                                                };
                                            }}
                                        />
                                    </Box>
                                </Center>
                                {selectedDate && selectedDateLabel && (
                                    <Stack gap={4} align="center" mt="xs">
                                        <Text size="lg" fw={600} ta="center">
                                            {selectedDateLabel}
                                        </Text>
                                        <Text size="xs" c="dimmed" ta="center">
                                            {userTimezone.replace(/_/g, ' ')}
                                        </Text>
                                    </Stack>
                                )}
                            </Stack>
                            <Divider />
                            <Flex justify="flex-end" flex={0}>
                                <Group>
                                    <Select
                                        maw={100}
                                        size="xs"
                                        placeholder="Time Format"
                                        data={[TimeFormat.h12, TimeFormat.h24]}
                                        value={timeFormat}
                                        onChange={(value) => updateTimeFormat(value as TimeFormat)}
                                        clearable={false}
                                    />
                                    <Select
                                        size="xs"
                                        maw={280}
                                        placeholder="Select your timezone"
                                        data={Intl.supportedValuesOf('timeZone').map((tz) => ({
                                            value: tz,
                                            label: tz.replace(/_/g, ' '),
                                        }))}
                                        searchable
                                        value={userTimezone}
                                        onChange={(value) => {
                                            if (value !== null) {
                                                setUserTimezone(value);
                                            }
                                        }}
                                        clearable={false}
                                    />
                                </Group>
                            </Flex>
                            {selectedDate ? (
                                <TimeSlotSelector
                                    availableSlots={availableSlots}
                                    selectedTime={selectedTime}
                                    setSelectedTime={setSelectedTime}
                                    timeFormat={timeFormat}
                                />
                            ) : (
                                <Text c="dimmed" ta="center" py="md">
                                    Select a date to view available time slots
                                </Text>
                            )}
                        </Stack>
                    </Paper>
                </Stack>
            }
            <Affix position={{ bottom: 0, right: 0 }}>
                <Transition transition="slide-up" mounted={selectedDate !== null && selectedTime !== null} duration={400}>
                    {(transitionStyles) => (
                        <div style={transitionStyles} >
                            <Paper w="100vw" shadow="lg" p="md" bg='primary.1'>
                                <Flex justify="flex-end" gap="md" align="center" direction={{ base: 'column', sm: 'row' }}>
                                    <Text ta="center">
                                        Selected slot:{' '}
                                        <Text span fw={600}>
                                            {selectedDate && selectedDateLabel ? `${selectedDateLabel} · ${selectedTime}` : `${selectedTime}`}
                                        </Text>
                                    </Text>
                                    <Group>
                                        <Button
                                            leftSection={<IconX />}
                                            style={transitionStyles}
                                            onClick={() => { setSelectedTime(null); }}
                                            variant="outline"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            leftSection={<IconCheck />}
                                            style={transitionStyles}
                                            onClick={cbConfirmClicked}
                                        >
                                            Confirm
                                        </Button>
                                    </Group>
                                </Flex>
                            </Paper>
                        </div>
                    )}
                </Transition>
            </Affix>
        </Container >
    );
}
